<template>
  <div class="farm-zone-select">
    <v-menu
      v-model="open"
      :close-on-content-click="false"
      :disabled="disabled"
      offset-y
      max-width="520"
      min-width="360"
      nudge-bottom="4"
    >
      <template v-slot:activator="{ attrs }">
        <v-text-field
          :value="displayLabel"
          :placeholder="$t('reports.cropProgression.select')"
          readonly
          outlined
          dense
          hide-details="auto"
          append-icon="mdi-menu-down"
          :disabled="disabled"
          v-bind="attrs"
          @click="onActivatorClick"
          @click:append="onActivatorClick"
        />
      </template>

      <v-card class="fz-popover">
        <div class="pa-3">
          <v-text-field
            v-model="search"
            :placeholder="$t('reports.cropProgression.search')"
            prepend-inner-icon="mdi-magnify"
            outlined
            dense
            hide-details
            clearable
            class="mb-2"
          />

          <div class="fz-list" ref="list" @scroll="onScroll">
            <div v-for="farm in farms" :key="farm.id" class="fz-farm">
              <div class="fz-row">
                <v-icon
                  :color="isPrimarySelected(farm) ? 'primary' : 'grey'"
                  class="mr-2"
                  @click="selectFarm(farm)"
                >
                  {{ isPrimarySelected(farm) ? "mdi-radiobox-marked" : "mdi-radiobox-blank" }}
                </v-icon>
                <span class="fz-name" @click="selectFarm(farm)">{{ farm.farmName }}</span>
                <v-btn
                  v-if="farm.accordionZones.length"
                  icon
                  small
                  class="ml-1"
                  @click="toggleExpand(farm)"
                >
                  <v-icon>{{ expandedFarmId === farm.id ? "mdi-chevron-up" : "mdi-chevron-down" }}</v-icon>
                </v-btn>
              </div>

              <!-- accordion: non-primary zones only -->
              <div v-if="expandedFarmId === farm.id && farm.accordionZones.length" class="fz-zones">
                <div v-for="zone in farm.accordionZones" :key="zone.geofenceId" class="fz-row fz-subrow">
                  <v-icon
                    :color="isZoneSelected(zone) ? 'primary' : 'grey'"
                    class="mr-2"
                    @click="selectZone(zone)"
                  >
                    {{ isZoneSelected(zone) ? "mdi-radiobox-marked" : "mdi-radiobox-blank" }}
                  </v-icon>
                  <span class="fz-name" @click="selectZone(zone)">{{ zone.zoneName }}</span>
                </div>
              </div>
            </div>

            <div v-if="loading" class="text-center py-2">
              <v-progress-circular indeterminate size="18" width="2" color="primary" />
            </div>
            <div v-if="!loading && !farms.length" class="grey--text text-center py-3">
              {{ $t("reports.cropProgression.noAvailableDates") }}
            </div>
          </div>
        </div>
      </v-card>
    </v-menu>
  </div>
</template>

<script>
import { getAllFarmWithZones } from "@/_services/OptionService";

export default {
  name: "FarmZoneSelect",
  props: {
    value: { type: Object, default: null },
    label: { type: String, default: "" },
    disabled: { type: Boolean, default: false },
  },
  data() {
    return {
      open: false,
      search: "",
      farms: [],
      loading: false,
      page: 1,
      limit: 20,
      hasMore: true,
      expandedFarmId: null,
      searchTimer: null,
      reqSeq: 0,
    };
  },
  computed: {
    displayLabel() {
      return this.value ? `${this.value.farmName} — ${this.value.zoneName || ""}`.trim() : "";
    },
  },
  watch: {
    search() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.page = 1;
        this.hasMore = true;
        this.fetchFarms(false);
      }, 500);
    },
    open(val) {
      // Prefetched in created(); only fetch on open if the background load hasn't
      // populated the list and isn't already running (avoids a duplicate request).
      if (val && !this.farms.length && !this.loading) this.fetchFarms(false);
    },
  },
  created() {
    this.fetchFarms(false);
  },
  methods: {
    // The readonly text-field is the menu activator; toggle the menu ourselves so
    // it opens on the first click (the default activator swallows it for focus).
    onActivatorClick() {
      if (!this.disabled) this.open = !this.open;
    },
    extractFarms(env) {
      if (Array.isArray(env)) return env;
      if (!env) return [];
      const d = env.data != null ? env.data : env.info;
      if (Array.isArray(d)) return d;
      if (d && Array.isArray(d.data)) return d.data;
      if (d && Array.isArray(d.info)) return d.info;
      return [];
    },
    normalizeFarm(farm) {
      // Some farms/zones are missing a name in the database — fall back to a
      // stable id-based label instead of rendering a blank row.
      const farmName =
        farm.farmName && String(farm.farmName).trim()
          ? farm.farmName
          : this.$t("reports.cropProgression.unnamedFarm", { id: farm.id });
      const rawZones = farm.segments || farm.zones || farm.geofences || [];
      const zones = rawZones.map((z) => ({
        ...z,
        geofenceId: z.id,
        farmId: farm.id,
        farmName,
        zoneName:
          z.geofenceName && String(z.geofenceName).trim()
            ? z.geofenceName
            : this.$t("reports.cropProgression.unnamedZone", { id: z.id }),
      }));
      return {
        id: farm.id,
        farmName,
        // Primary segment is auto-selected on farm-select; kept out of the accordion.
        primaryZone: zones.find((z) => z.isPrimary) || null,
        accordionZones: zones.filter((z) => !z.isPrimary),
      };
    },
    async fetchFarms(append = false) {
      // Sequence guard: a newer request supersedes older in-flight ones, so a
      // search typed while a fetch is pending always wins (no dropped queries,
      // no out-of-order results applied). Append is additionally gated in onScroll.
      const seq = ++this.reqSeq;
      this.loading = true;
      try {
        const env = await getAllFarmWithZones({
          search: this.search || "",
          page: this.page,
          limit: this.limit,
        });
        if (seq !== this.reqSeq) return; // superseded by a newer request
        const raw = this.extractFarms(env);
        const list = raw.map(this.normalizeFarm);
        this.farms = append ? [...this.farms, ...list] : list;
        this.hasMore = raw.length === this.limit;
      } catch (e) {
        if (seq === this.reqSeq) this.hasMore = false;
      } finally {
        if (seq === this.reqSeq) this.loading = false;
      }
    },
    onScroll(e) {
      if (!this.hasMore || this.loading) return;
      const el = e.target;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) {
        this.page += 1;
        this.fetchFarms(true);
      }
    },
    isPrimarySelected(farm) {
      return (
        this.value &&
        farm.primaryZone &&
        this.value.geofenceId === farm.primaryZone.geofenceId
      );
    },
    isZoneSelected(zone) {
      return this.value && this.value.geofenceId === zone.geofenceId;
    },
    selectFarm(farm) {
      // Auto-select the primary segment; keep the popover open so the user can
      // still expand the accordion and pick a specific zone instead.
      this.expandedFarmId = farm.id;
      if (farm.primaryZone) {
        this.$emit("input", farm.primaryZone);
        if (!farm.accordionZones.length) this.open = false;
      }
    },
    toggleExpand(farm) {
      this.expandedFarmId = this.expandedFarmId === farm.id ? null : farm.id;
    },
    selectZone(zone) {
      this.$emit("input", zone);
      this.open = false;
    },
  },
};
</script>

<style scoped>
.fz-popover {
  overflow: hidden;
}
.fz-list {
  max-height: 320px;
  overflow-y: auto;
}
.fz-row {
  display: flex;
  align-items: center;
  padding: 8px 4px;
  border-bottom: 1px solid #f0f0f0;
}
.fz-subrow {
  padding-left: 28px;
}
.fz-name {
  cursor: pointer;
  color: #1e1e1e;
  font-size: 14px;
}
.fz-zones {
  background: #fafafa;
}
</style>
